import {
    MutableRefObject,
    ReactNode,
    useState,
    useRef,
    useEffect
} from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";
import { OptionalExceptFor } from "@/utils/typescript-utils.ts";

import { TEAM_DATA, Team } from "./how-it-works-tabs-content/tabs-content";
import TestimonialQuote, { TestimonialQuoteProps } from "./testimonial-quote";

type ActiveTeamDataProps = OptionalExceptFor<
    Team,
    | "heroImage"
    | "contentTitle"
    | "contentText"
    | "quoteColor"
    | "quoteText"
    | "customerName"
    | "customerTitle"
>;

export const HowItWorks = () => {
    const intl = useIntl();

    const cardRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

    const [activeTeamIndex, setActiveTeamIndex] = useState<number>(0);

    const [activeTeamData, setActiveTeamData] = useState<ActiveTeamDataProps>(
        TEAM_DATA[0] as ActiveTeamDataProps
    );

    useEffect(() => {
        setActiveTeamData(TEAM_DATA[activeTeamIndex] as ActiveTeamDataProps);
    }, [activeTeamIndex]);

    const renderActiveMedia = () => {
        const {
            heroImage,
            contentTitle,
            contentText,
            quoteColor,
            quoteText,
            customerName,
            customerTitle
        } = activeTeamData;

        const quoteProps: TestimonialQuoteProps = {
            quoteColor: quoteColor,
            quoteText: intl.formatMessage(quoteText),
            customerName: intl.formatMessage(customerName),
            customerTitle: intl.formatMessage(customerTitle)
        };

        return (
            <>
                <Row layout="4-8">
                    <div>
                        <h3>{intl.formatMessage(contentTitle)}</h3>
                        <p>{intl.formatMessage(contentText)}</p>
                        <div css={quoteStyles}>
                            <TestimonialQuote {...quoteProps} />
                        </div>
                    </div>
                    <div css={imageOverrideStyles}>
                        <Picture
                            image={{
                                src: heroImage,
                                alt: ""
                            }}
                        />
                    </div>
                </Row>

                <div css={tabletQuoteStyles}>
                    <Row layout="8">
                        <TestimonialQuote {...quoteProps} />
                    </Row>
                </div>
            </>
        );
    };

    return (
        <UFOSegment name="homepage-v2-hero">
            <section id="hero-jira-cta" css={jiraHeroStyles}>
                <Row>
                    <div>
                        <h2 css={heroTitleStyles}>
                            <span css={heroTitleSpanStyles}>
                                <FormattedMessage
                                    id="JiraProductDiscoveryPage.howItWorks.header"
                                    defaultMessage="How it works"
                                    values={{
                                        teams: (str: ReactNode) => (
                                            <Adg4TextUnderline
                                                underline={
                                                    <svg
                                                        width="100%"
                                                        height="30"
                                                        viewBox="0 0 306 42"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M0.314542 22.3672C52.9464 14.9125 91.8245 33.6666 144.966 18.1254C147.46 24.7349 153.892 33.0058 163.497 33.1094C155.486 32.9725 142.641 15.7178 155.536 27.2686C163.119 36.0683 196.172 25.2364 170.842 32.944C188.334 31.2885 204.356 20.5339 216.163 10.9702C235.811 -2.65832 220.928 23.6737 235.178 36.8756C255.37 55.2242 316.463 7.29572 304.134 7.28816C296.296 13.2947 285.769 17.046 281.423 23.6985C204.757 63.1757 259.992 -9.72674 214.311 2.55426C139.744 53.2533 177.393 7.4163 133.572 10.6223C108.06 28.9946 -0.613806 0.46356 0.29434 22.3672L0.314542 22.3672Z"
                                                            fill="#FFA900"
                                                        />
                                                    </svg>
                                                }
                                            >
                                                {str}
                                            </Adg4TextUnderline>
                                        ),
                                        br: <br />
                                    }}
                                />
                            </span>
                        </h2>
                        <div ref={cardRef}>
                            <div>
                                <Adg4Tabs>
                                    <Adg4DraggableTabList css={[tabsStyle]}>
                                        {TEAM_DATA.map(
                                            (
                                                {
                                                    title,
                                                    id,
                                                    icon,
                                                    englishLabel
                                                },
                                                index
                                            ) => {
                                                return (
                                                    <Adg4DraggableTab
                                                        key={id}
                                                        onClick={() => {
                                                            setActiveTeamIndex(
                                                                index
                                                            );
                                                        }}
                                                    >
                                                        <button
                                                            css={
                                                                buttonResetStyle
                                                            }
                                                            data-product-key="home"
                                                            data-event-component="button"
                                                            data-event-container="heroSection"
                                                            data-event="clicked"
                                                            data-label-english={
                                                                englishLabel
                                                            }
                                                            aria-label={intl.formatMessage(
                                                                title
                                                            )}
                                                        >
                                                            <div
                                                                css={[
                                                                    teamCardStyles,
                                                                    activeTeamIndex ===
                                                                        index &&
                                                                        teamCardActiveStyles
                                                                ]}
                                                            >
                                                                <div
                                                                    css={[
                                                                        iconContainerStyles
                                                                    ]}
                                                                    className="default-icon"
                                                                >
                                                                    {
                                                                        <Picture
                                                                            image={{
                                                                                src: `${icon}`,
                                                                                alt: "",
                                                                                draggable:
                                                                                    "false"
                                                                            }}
                                                                        />
                                                                    }
                                                                </div>
                                                                <div
                                                                    css={[
                                                                        iconContainerStyles
                                                                    ]}
                                                                    className="hover-icon"
                                                                >
                                                                    <Picture
                                                                        image={{
                                                                            src: icon,
                                                                            alt: "",
                                                                            draggable:
                                                                                "false"
                                                                        }}
                                                                    />
                                                                </div>
                                                                <span
                                                                    css={
                                                                        teamCardTextStyles
                                                                    }
                                                                >
                                                                    {intl.formatMessage(
                                                                        title
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    </Adg4DraggableTab>
                                                );
                                            }
                                        )}
                                    </Adg4DraggableTabList>
                                </Adg4Tabs>
                            </div>
                        </div>
                        <>{renderActiveMedia()}</>
                    </div>
                </Row>
            </section>
        </UFOSegment>
    );
};

const jiraHeroStyles = css({
    overflow: "hidden",
    marginBlockEnd: "40px"
});

const heroTitleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "40px",
    fontWeight: 700,
    lineHeight: "48px",
    textAlign: "center"
});

const heroTitleSpanStyles = css({
    "position": "relative",

    "::after": {
        position: "absolute",
        width: "101px",
        height: "37px",
        insetBlockEnd: "-5px",
        insetInlineEnd: "-90px",
        content: "",
        backgroundImage:
            "url(https://dam-cdn.atl.orangelogic.com/AssetLink/bkr1v00185h113ro7g57yu2ih1rc6gd6.svg)",

        [media.below.md]: {
            width: "83px",
            height: "30px",
            backgroundSize: "83px 30px",
            insetBlockEnd: "-5px",
            insetInlineEnd: "-72px"
        }
    }
});

const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});

const tabsStyle = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    marginBlockEnd: "50px",
    gap: "48px"
});

const teamCardStyles = css({
    "boxSizing": "border-box",
    "display": "flex",
    "flexDirection": "column",
    "width": "173px",
    "alignItems": "center",
    "padding": "6px",
    "gap": "0px",
    "borderRadius": "6px",
    "border": "1px",
    "background": token("color.background.neutral"),

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".hover-icon": {
        display: "none"
    },

    ":hover": {
        "background": token("color.background.neutral.hovered"),

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".default-icon": {
            display: "none"
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hover-icon": {
            display: "block"
        }
    }
});

const iconContainerStyles = css({
    height: "62px",
    width: "62px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "62px",
        height: "62px"
    }
});

const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: "#101214"
});

const teamCardActiveStyles = css({
    border: "1px solid #101214",
    borderRadius: "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontWeight: "700"
    }
});

const imageOverrideStyles = css({
    // The picture element causes the sizing of images to be blown up in mobile view without this
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: { width: "100%", height: "auto" }
});

const quoteStyles = css({ [media.only.sm]: { display: "none" } });

const tabletQuoteStyles = css({
    [media.above.md]: { display: "none" },
    [media.below.sm]: { display: "none" }
});
